import type Cropper from 'cropperjs';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import globalLogger from '../../../../../../logger';
import type { GenericReduxDispatch } from '../../../../../../../common/actions/actionTypes';
import type { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import { getElementId } from '../../../../../../../common/elements/utils/elementPropertyUtils';
import { updateElement } from '../../../../../actions/elementActions';
import { readCurrentElementFromStore } from '../../../utils/imageModalElementStoreUtils';
import { getImageEditorDataObject } from '../../../imageModalUtils';
import {
    BackgroundRemovalVariantOptions,
    buildStagedBackgroundRemovalChanges,
    hasBackgroundRemovalRenderedVariant,
    isBackgroundRemovalActive,
} from '../../../backgroundRemoval/imageModalBackgroundRemovalUtils';

const logger = globalLogger.createChannel('image.modal.background');

type UseImageModalV2BackgroundSaveOptions = {
    element: ImMNElement;
    stagedActive: boolean;
    setStagedActive: (active: boolean) => void;
    // `currentElement` is the store's current element — staging can run ahead of a React re-render (the
    // first removal dispatches its source cache a moment before staging), so rendered-variant creation must not
    // rely on the hook's possibly stale element prop.
    createInactiveRenderedVariant: (imageEditorData: Cropper.Data, currentElement: ImMNElement) => Promise<boolean>;
    cancelEditing: () => void;
    // `shouldWaitForViewLoad` is true only when this save produced a brand-new render whose bytes still
    // need to arrive, so the caller holds the frozen editor frame until the View has loaded it. A save
    // that reused an already-cached variant passes false (still a successful save) so the caller finishes
    // immediately rather than waiting out a load that resolves instantly.
    afterSave: (shouldWaitForViewLoad?: boolean) => void;
};

/** Owns Background mode's staged variant preparation, discard, and persistence. */
export const useImageModalV2BackgroundSave = ({
    element,
    stagedActive,
    setStagedActive,
    createInactiveRenderedVariant,
    cancelEditing,
    afterSave,
}: UseImageModalV2BackgroundSaveOptions) => {
    const dispatch = useDispatch<GenericReduxDispatch>();
    const elementId = getElementId(element);
    const [isCreatingRenderedVariant, setIsCreatingRenderedVariant] = useState(false);

    const getCurrentElement = useCallback(
        () => readCurrentElementFromStore(dispatch, elementId, element),
        [dispatch, element, elementId],
    );

    const prepareVariant = useCallback(
        async (
            targetActive: boolean,
            onCreateRenderedVariantStart?: () => void,
        ): Promise<{ ready: boolean; didCreateRenderedVariant: boolean }> => {
            const currentElement = getCurrentElement();
            if (isBackgroundRemovalActive(currentElement) === targetActive) {
                return { ready: true, didCreateRenderedVariant: false };
            }

            const targetVariant = targetActive
                ? BackgroundRemovalVariantOptions.withoutBackground
                : BackgroundRemovalVariantOptions.withBackground;
            const imageEditorData = getImageEditorDataObject(currentElement);

            if (!imageEditorData || hasBackgroundRemovalRenderedVariant(currentElement, targetVariant)) {
                return { ready: true, didCreateRenderedVariant: false };
            }

            onCreateRenderedVariantStart?.();
            const didCreateRenderedVariant = await createInactiveRenderedVariant(
                imageEditorData as Cropper.Data,
                currentElement,
            );
            return { ready: didCreateRenderedVariant, didCreateRenderedVariant };
        },
        [getCurrentElement, createInactiveRenderedVariant],
    );

    const changeStagedActive = useCallback(
        async (active: boolean) => {
            setIsCreatingRenderedVariant(true);
            try {
                const prepared = await prepareVariant(active);
                if (prepared.ready) setStagedActive(active);
            } catch (error) {
                // Sensitive data audit: SAFE — logger sanitises media URLs and normalises errors.
                logger.error('Failed to prepare cropped background-removal variant', error);
            } finally {
                setIsCreatingRenderedVariant(false);
            }
        },
        [prepareVariant, setStagedActive],
    );

    const save = useCallback(
        async (onCreateRenderedVariantStart?: () => void) => {
            setIsCreatingRenderedVariant(true);
            let didCreateRenderedVariant = false;
            try {
                const prepared = await prepareVariant(stagedActive, onCreateRenderedVariantStart);
                if (!prepared.ready) return false;
                didCreateRenderedVariant = prepared.didCreateRenderedVariant;
                const currentElement = getCurrentElement();
                const changes = buildStagedBackgroundRemovalChanges({ element: currentElement, active: stagedActive });

                if (changes) dispatch(updateElement({ id: elementId, changes, silent: true, sync: true }));
            } catch (error) {
                // Sensitive data audit: SAFE — logger sanitises media URLs and normalises errors.
                logger.error('Failed to save background-removal variant', error);
                return false;
            } finally {
                setIsCreatingRenderedVariant(false);
            }

            // Only hold the frozen editor frame for the View load when this save produced a brand-new render:
            // its bytes still need to arrive, so switching to View immediately would flash the previous
            // variant. When the target render was already cached, the View swaps to it instantly, so finish
            // right away instead of waiting out the load — otherwise every cached toggle-save lags visibly.
            afterSave(didCreateRenderedVariant);
            return true;
        },
        [afterSave, dispatch, elementId, getCurrentElement, prepareVariant, stagedActive],
    );

    const discard = useCallback(() => {
        setStagedActive(isBackgroundRemovalActive(element));
        cancelEditing();
    }, [cancelEditing, element, setStagedActive]);

    return { isCreatingRenderedVariant, changeStagedActive, save, discard };
};
